/**
 * Customers, by business: who orders from you, and how to reach them. A customer is also made on
 * the spot from a new order; this page is for adding one ahead, and keeping details current.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { businessesQuery } from "@/features/settings/queries";
import { fieldMessage } from "@/lib/fieldMessage";
import { customersQuery, useCreateCustomer, useUpdateCustomer, type Customer } from "./queries";

const customerSchema = z.object({
  business_id: z.string().min(1, "orders.errors.businessRequired"),
  name: z.string().trim().min(1, "orders.errors.nameRequired").max(160, "orders.errors.tooLong"),
  email: z.union([z.literal(""), z.email("orders.errors.emailInvalid")]),
  phone: z.string().trim().max(40, "orders.errors.tooLong"),
});
type CustomerValues = z.infer<typeof customerSchema>;

export function CustomersPage() {
  const { t } = useTranslation();
  const customers = useQuery(customersQuery);
  const businesses = useQuery(businessesQuery);
  const [adding, setAdding] = useState(false);
  const businessName = (id: string) =>
    businesses.data?.find((business) => business.id === id)?.name ?? "";
  const several = (businesses.data?.length ?? 0) > 1;

  return (
    <section className="space-y-5">
      <Link
        to="/orders"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("orders.title")}
      </Link>
      <h1 className="text-xl font-semibold">{t("orders.customers.title")}</h1>

      {customers.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {customers.isError && <Notice tone="error">{t("orders.errors.loadFailed")}</Notice>}
      {customers.data?.length === 0 && <Notice tone="info">{t("orders.customers.empty")}</Notice>}

      {customers.data && customers.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {customers.data.map((customer) => (
            <CustomerRow
              key={customer.id}
              customer={customer}
              business={several ? businessName(customer.business_id) : ""}
            />
          ))}
        </ul>
      )}

      {adding ? (
        <CustomerForm onDone={() => setAdding(false)} />
      ) : (
        <Button type="button" variant="secondary" onClick={() => setAdding(true)}>
          {t("orders.customers.add")}
        </Button>
      )}
    </section>
  );
}

function CustomerRow({ customer, business }: { customer: Customer; business: string }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li className="px-4 py-3">
        <CustomerForm customer={customer} onDone={() => setEditing(false)} />
      </li>
    );
  }
  const contact = [customer.phone, customer.email, business].filter(Boolean).join(" · ");
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-stone-800">{customer.name}</p>
        {contact && <p className="truncate text-sm text-stone-500">{contact}</p>}
      </div>
      <button
        type="button"
        className="text-brand-700 text-sm underline"
        onClick={() => setEditing(true)}
      >
        {t("orders.edit")}
      </button>
    </li>
  );
}

/** Adds a customer, or edits one: a customer keeps their business, so it is chosen only once. */
function CustomerForm({ customer, onDone }: { customer?: Customer; onDone: () => void }) {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const active = (businesses.data ?? []).filter((business) => business.is_active);
  const form = useForm<CustomerValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      business_id: customer?.business_id ?? (active.length === 1 ? (active[0]?.id ?? "") : ""),
      name: customer?.name ?? "",
      email: customer?.email ?? "",
      phone: customer?.phone ?? "",
    },
  });
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      if (customer) {
        await update.mutateAsync({
          id: customer.id,
          name: values.name,
          email: values.email,
          phone: values.phone,
        });
      } else {
        await create.mutateAsync({ ...values, notes: "" });
      }
      onDone();
    } catch (error) {
      form.setError("root", {
        message: error instanceof ApiError ? error.message : t("orders.errors.saveFailed"),
      });
    }
  });

  return (
    <form
      onSubmit={submit}
      noValidate
      className="space-y-3 rounded-lg border border-stone-200 bg-white p-4"
    >
      {!customer && active.length > 1 && (
        <Field
          name="customer-business"
          label={t("orders.new.business")}
          error={fieldMessage(errors, "business_id", t)}
        >
          <Select id="customer-business" {...form.register("business_id")}>
            <option value="">{t("orders.new.chooseBusiness")}</option>
            {active.map((business) => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field
        name="customer-name"
        label={t("orders.customers.name")}
        error={fieldMessage(errors, "name", t)}
      >
        <Input id="customer-name" autoComplete="off" {...form.register("name")} />
      </Field>
      <Field
        name="customer-phone"
        label={t("orders.customers.phone")}
        error={fieldMessage(errors, "phone", t)}
      >
        <Input id="customer-phone" type="tel" {...form.register("phone")} />
      </Field>
      <Field
        name="customer-email"
        label={t("orders.customers.email")}
        error={fieldMessage(errors, "email", t)}
      >
        <Input id="customer-email" type="email" {...form.register("email")} />
      </Field>
      {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}
      <div className="flex gap-2">
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("common.saving") : t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}
